import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh';
import SceneManager from './scene.js';
import InputController from '../controllers/input-controller.js';
import SelectionController from '../controllers/selection-controller.js';
import DragDropManager from '../controllers/drag-drop-manager.js';
import { audioCacheManager } from '../controllers/audio-cache-manager.js';

// BVH SETUP
// Estende Three.js una volta sola all'avvio dell'applicazione:
//  • computeBoundsTree()  → costruisce la BVH sulla geometria (chiamato in Furniture)
//  • disposeBoundsTree()  → libera la BVH (chiamato in Furniture.dispose)
//  • acceleratedRaycast   → sovrascrive Mesh.raycast() per usare la BVH in modo transparente
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
THREE.Mesh.prototype.raycast = acceleratedRaycast;

class Engine {
  constructor(container) {
    this.container = container;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.controls = null;
    this.inputController = null;
    this.selectionController = null;
    this.dragDropManager = null;
    this.clock = new THREE.Clock();
    this.isRunning = false;

    // Render on demand: renderizza solo quando la scena è cambiata
    this.needsRender = true;
  }

  init() {
    this.setupRenderer();
    this.setupCamera();
    this.setupScene();
    this.setupEnvironment();
    this.setupControls();
    this.setupControllers();
    this.setupEventListeners();

    // Espone requestRender globalmente per door.js, window.js, ecc.
    window.requestRender = () => this.requestRender();

    console.log('✔ Engine initialized');
  }

  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });
    this.renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.renderer.shadowMap.enabled = false;

    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.4;

    if (THREE.SRGBColorSpace !== undefined) {
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    } else {
      this.renderer.outputEncoding = THREE.sRGBEncoding;
    }

    this.container.appendChild(this.renderer.domElement);
  }

  setupCamera() {
    const aspect = this.container.clientWidth / this.container.clientHeight;
    this.camera = new THREE.PerspectiveCamera(60, aspect, 0.1, 1000);
    this.camera.position.set(0, 12, 20);
    this.camera.lookAt(0, 0, 0);
  }

  setupScene() {
    this.scene = new SceneManager();
    this.scene.init();
  }

  // Genera una environment map procedurale con RoomEnvironment (Three.js built-in)
  setupEnvironment() {
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    pmremGenerator.compileEquirectangularShader();

    const roomEnv = new RoomEnvironment(this.renderer);
    const envTexture = pmremGenerator.fromScene(roomEnv, 0.04).texture;

    const threeScene = this.scene.getScene();
    // IBL per tutti i materiali PBR
    threeScene.environment = envTexture;
    threeScene.background  = new THREE.Color(0x1a1a2e);

    pmremGenerator.dispose();
    roomEnv.dispose();
  }

  setupControls() {
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);

    // Damping disabilitato: stop immediato al rilascio del mouse
    this.controls.enableDamping  = false;
    this.controls.screenSpacePanning = true;
    this.controls.minDistance = 5;
    this.controls.maxDistance = 50;
    this.controls.maxPolarAngle = Math.PI / 2.1;

    // Qualsiasi movimento camera → richiede un nuovo frame
    this.controls.addEventListener('change', () => this.requestRender());
  }

  setupControllers() {
    const wallController = this.scene.getWallController();
    const doorController = this.scene.getDoorController();
    const windowController = this.scene.getWindowController();
    const furnitureController = this.scene.getFurnitureController();

    wallController.setControls(this.controls);
    wallController.setDoorController(doorController);
    wallController.setWindowController(windowController);

    this.inputController = new InputController(
      this.camera,
      this.renderer.domElement,
      wallController,
      doorController,
      windowController
    );

    this.selectionController = new SelectionController(
      this.camera,
      this.renderer.domElement,
      furnitureController,
      this.controls
    );

    this.selectionController.setDoorController(doorController);
    this.selectionController.setWindowController(windowController);
    this.selectionController.setWallController(wallController);

    this.dragDropManager = new DragDropManager(
      this.camera,
      this.renderer.domElement,
      furnitureController,
      wallController,
      doorController,
      windowController
    );
  }

  setupEventListeners() {
    window.addEventListener('resize', () => this.onResize());

    // Qualsiasi interazione sul canvas (drag, preview, hover, selezione) → render
    this.renderer.domElement.addEventListener('mousemove',
      () => this.requestRender()
    );
    this.renderer.domElement.addEventListener('touchmove',
      () => this.requestRender(), { passive: true }
    );

    // Click e touch → aggiornano la scena
    this.renderer.domElement.addEventListener('click',
      () => this.requestRender()
    );
    this.renderer.domElement.addEventListener('touchend',
      () => this.requestRender(), { passive: true }
    );
  }

  onResize() {
    const width  = this.container.clientWidth;
    const height = this.container.clientHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
    this.requestRender();
  }
  
  // Segna la scena come "sporca": verrà renderizzata al prossimo frame
  // Da chiamare ovunque la scena cambi: aggiunta mobili, animazioni, selezione, ecc.
  requestRender() {
    this.needsRender = true;
  }

  getWallController()       { return this.scene.getWallController(); }
  getDoorController()       { return this.scene.getDoorController(); }
  getWindowController()     { return this.scene.getWindowController(); }
  getFurnitureController()  { return this.scene.getFurnitureController(); }
  getSelectionController()  { return this.selectionController; }
  getInputController()      { return this.inputController; }

  start() {
    this.isRunning   = true;
    this.needsRender = true; // primo render garantito
    this.animate();
  }

  stop() {
    this.isRunning = false;
  }

  animate() {
    if (!this.isRunning) return;
    requestAnimationFrame(() => this.animate());

    // Render on demand: salta il draw call se la scena non è cambiata
    if (!this.needsRender) return;
    this.needsRender = false;

    // controls.update() non serve senza damping; chiamato solo per API consistency
    // (ad esempio plugin che estendono OrbitControls potrebbero richiederlo)
    this.scene.update(this.clock.getDelta());
    this.renderer.render(this.scene.getScene(), this.camera);
  }

  dispose() {
    this.stop();
    this.controls.dispose();
    this.renderer.dispose();
    this.scene.dispose();
    if (this.inputController)     this.inputController.dispose();
    if (this.selectionController) this.selectionController.dispose();
    if (this.dragDropManager)     this.dragDropManager.dispose();
    if (this.container.contains(this.renderer.domElement)) {
      this.container.removeChild(this.renderer.domElement);
    }
    audioCacheManager.dispose();
    delete window.requestRender;
  }
}

export default Engine;